import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { Box, Typography, Paper } from "@mui/material";
import { getUrl } from "../../../Utils/Common";
import FacebookCircularProgress from "../../shared/shared/Loading";
import CustomSelectBox from "../../shared/shared/CustomSelectBox";
import DateRangePicker from "../ActualVsExpectedMixChart/DatePicker";
import dayjs from "dayjs";
import useRefreshData from "../../../Utils/CustomHooks/useRefreshData";

const TopLossMakingPromotionsChart = () => {
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [selectedHierarchy, setSelectedHierarchy] = useState("sku_db");
  const [startDate, setStartDate] = useState(dayjs().subtract(12, "months").startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [timePeriod, setTimePeriod] = useState("P12M");
  const [target, setTarget] = useState("12");
  const [selectedTab, setSelectedTab] = useState(2);
  const projectId = useSelector((state) => state.userProject.projectId);

  const resetChartData = () => {
    setSeries([]);
    setCategories([]);
  };

  const resetChartError = () => {
    setError(null);
  };

  const resetChartLoading = () => {
    setLoading(false);
  };

  const fetchData = async () => {
    if (!projectId) {
      setError("Project ID not found.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const apiUrl = `${getUrl()}/strategy_creation/top_low_impact_promos?project_id=${projectId}&hierarchy=${selectedHierarchy}&time_period=${timePeriod}&target=${target}&top_n=10&filters={}`;
      
      console.log("API URL:", apiUrl);
      
      const response = await fetch(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
      });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
        }
        
        const data = await response.json();

        console.log("API response:", data);

        if (!data || typeof data !== "object") {
          throw new Error("Invalid API response format");
        }

        let itemNames, baselineVolumeData, volumeSalesData;

        if (data[selectedHierarchy] && data.baseline_volume && data.volume_sales) {
          itemNames = data[selectedHierarchy];
          baselineVolumeData = data.baseline_volume;
          volumeSalesData = data.volume_sales;
        } else if (Array.isArray(data)) {
          itemNames = data.map(item => item[selectedHierarchy] || item.sku || item.id);
          baselineVolumeData = data.map(item => item.baseline_volume || 0);
          volumeSalesData = data.map(item => item.volume_sales || 0);
        } else {
          const { date, ...itemData } = data;
          const itemKeys = Object.keys(itemData);
          if (itemKeys.length === 0) throw new Error(`No ${selectedHierarchy} data found`);
          
          itemNames = itemKeys.slice(0, 10);
          volumeSalesData = itemNames.map((itemName) => {
            const itemValues = itemData[itemName] || [];
            return itemValues.reduce((sum, v) => sum + (v || 0), 0);
          });
          baselineVolumeData = volumeSalesData.map((v) => v * 0.25);
        }

        setCategories(itemNames);

        const chartSeries = [
          {
            name: "Actual Volume",
            data: volumeSalesData,
          },
          {
            name: "Baseline Volume",
            data: baselineVolumeData,
          },
        ];

        setSeries(chartSeries);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Failed to load data");
        setSeries([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

  const { handleRefresh, isRefreshing } = useRefreshData(
    fetchData,
    [resetChartData, resetChartError, resetChartLoading]
  );

  useEffect(() => {
    function fetchHierarchyLevels() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        return;
      }
      fetch(`${getUrl()}/market_overview/hierarchy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({ project_id: projectId }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          console.log("result from hierarchy", result);
          
          if (!result["0"] || Object.keys(result["0"]).length === 0) {
            throw new Error("No hierarchy data available");
          }
          
          const allowedHierarchyLevels = ["brand", "class", "manufacturer", "sku_db"];
          const rawHierarchyLevels = Object.keys(result["0"] || {}); 
          const hierarchyLevels = rawHierarchyLevels.filter((key) =>
            allowedHierarchyLevels.includes(key)
          );
          hierarchyLevels.push("channel", "retailer");
          
          setHierarchyOptions(hierarchyLevels);
          
          if (hierarchyLevels.includes("sku_db")) {
            setSelectedHierarchy("sku_db");
          } else if (hierarchyLevels.length > 0) {
            setSelectedHierarchy(hierarchyLevels[0]);
          }
        })
        .catch((error) => {
          console.error("Error fetching hierarchy levels:", error);
        });
    }

    fetchHierarchyLevels();
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [projectId, selectedHierarchy, timePeriod, target]);

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: { show: false },
      fontFamily: "Barlow Semi Condensed, sans-serif",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
      },
    },
    colors: ["#255e91", "#53b5ff"], 
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories,
      labels: { 
        rotate: -45, 
        style: { fontSize: "10px" },
        hideOverlappingLabels: false,
        maxHeight: 120,
      },
      title: {
        text: selectedHierarchy === "sku_db" ? "SKU ID" : 
              selectedHierarchy === "class" ? "Class" :
              selectedHierarchy === "brand" ? "Brand" :
              selectedHierarchy === "manufacturer" ? "Manufacturer" :
              selectedHierarchy === "channel" ? "Channel" :
              selectedHierarchy === "retailer" ? "Retailer" :
              selectedHierarchy.charAt(0).toUpperCase() + selectedHierarchy.slice(1),
        style: { fontSize: "14px", fontWeight: "bold" },
        offsetY: 10,
      },
    },
    yaxis: {
      title: {
        text: "Volume",
        style: { fontSize: "14px", fontWeight: "bold" },
      },
      labels: {
        formatter: (val) => val.toFixed(0),
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      markers: { width: 10, height: 10 },
    },
    grid: {
      borderColor: "#f0f0f0",
      strokeDashArray: 3,
      padding: {
        bottom: 30,
        left: 30,
      },
    },
    tooltip: { shared: true, intersect: false },
  };

  return (
    <Paper sx={{ p: 2, width: "100%", maxWidth: "100%" }}>
      <Typography
        align="center"
        fontWeight={500}
        mb={1}
        sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, pb: 1 }}
      >
        Top Loss-Making or Low-Impact Promotions
      </Typography>

      {/* Filters Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <CustomSelectBox
          disabled={true}
          value={selectedHierarchy}
          onChange={(event) => {
            setSelectedHierarchy(event.target.value);
          }}
          options={hierarchyOptions}
          sx={{
            fontWeight: 500,
            fontSize: "18px",
            height: 40,
            minHeight: 40,
            maxHeight: 40,
            "& .MuiOutlinedInput-root": {
              height: 40,
            },
          }}
        />

        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          setTimePeriod={setTimePeriod}
          setTarget={setTarget}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          disabled={true}
        />
      </Box>

      {loading || isRefreshing ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <FacebookCircularProgress loading={true} />
        </Box>
      ) : error || !series || series.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={error || "No data available"}
          />
        </Box>
      ) : (
        <Box sx={{ width: "100%", minHeight: 500, overflow: "auto" }}>
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={500}
            width={800}
          />
        </Box>
      )}
    </Paper>
  );
};

export default TopLossMakingPromotionsChart;
